import { serializeJsonlLine } from '../utils/jsonl.js'

// The legacy command dispatcher uses COMMAND_LINE_MAX for one text command.
// JSONL file parsing is a separate path in current firmware and uses an
// 8192-byte logical line budget (see openhasp-next/include/hasp_conf.h).
export const FIRMWARE_COMMAND_LINE_MAX = 256
export const FIRMWARE_JSONL_MAX_LINE_BYTES = 8192
export const FIRMWARE_FILE_JSONL_MAX_LINE_BYTES = FIRMWARE_JSONL_MAX_LINE_BYTES
export const FIRMWARE_LEGACY_COMMAND_MAX_LINE_BYTES = FIRMWARE_COMMAND_LINE_MAX - 1
// Current user-setups define MQTT_MAX_PACKET_SIZE=2048. The firmware rejects
// payloads when length + 1 >= that buffer size, hence 2046 usable bytes.
export const FIRMWARE_MQTT_MAX_PACKET_SIZE = 2048
export const FIRMWARE_MQTT_MAX_JSONL_PAYLOAD_BYTES = FIRMWARE_MQTT_MAX_PACKET_SIZE - 2

export function firmwareRecordByteLength(record) {
    return new TextEncoder().encode(serializeJsonlLine(record)).length
}

/**
 * Validate the current firmware's complete JSONL file/parser budget without
 * applying the legacy 255-byte split policy used by connection/badge
 * compatibility records. Records remain in the returned array so an error
 * can never silently discard a GPIO binding.
 */
export function validateFirmwareJsonlLineLimit(records, maxBytes = FIRMWARE_FILE_JSONL_MAX_LINE_BYTES) {
    const oversized = (records ?? []).filter(record => firmwareRecordByteLength(record) > maxBytes)
    return {
        valid: oversized.length === 0,
        errors: oversized.map(record =>
            `Firmware JSONL record${record?.id === undefined ? '' : ` id=${record.id}`} is ${firmwareRecordByteLength(record)} bytes; maximum is ${maxBytes}.`),
    }
}

function recordContexts(records) {
    let page
    return records.map(record => {
        if (record?.page !== undefined && record?.id === undefined) page = record.page
        return { page, record }
    })
}

/**
 * Reject oversized firmware records. Split connection and badge records are
 * atomic units: reject the whole page/id unit rather than leave partial state.
 */
export function enforceFirmwareLineLimit(records) {
    const contexts = recordContexts(records)
    const badgeUnits = new Set(contexts
        .filter(({ record }) => record?.obj === 'badge')
        .map(({ page, record }) => `${page}:${record.id}`))
    const oversized = contexts.filter(({ page, record }) =>
        (record?.obj === 'connection' || Array.isArray(record?.points)
            || badgeUnits.has(`${page}:${record?.id}`))
        && firmwareRecordByteLength(record) > FIRMWARE_LEGACY_COMMAND_MAX_LINE_BYTES)
    const rejectedConnections = new Set(oversized
        .filter(({ record }) => record?.obj === 'connection' || Array.isArray(record?.points))
        .map(({ page, record }) => `${page}:${record.id}`))
    const rejectedBadges = new Set(oversized
        .filter(({ page, record }) => badgeUnits.has(`${page}:${record.id}`))
        .map(({ page, record }) => `${page}:${record.id}`))
    const rejectedRecords = new Set(oversized.map(({ record }) => record))
    const errors = oversized.map(({ page, record }) => {
        const label = badgeUnits.has(`${page}:${record?.id}`)
            ? `badge id=${record.id}`
            : record?.obj === 'connection' || Array.isArray(record?.points)
                ? `connection id=${record.id}`
            : `${record?.obj ?? 'record'}${record?.id === undefined ? '' : ` id=${record.id}`}`
        return `Firmware export record for ${label} on page ${page} is ${firmwareRecordByteLength(record)} bytes; maximum is ${FIRMWARE_LEGACY_COMMAND_MAX_LINE_BYTES}.`
    })
    return {
        records: contexts
            .filter(({ page, record }) => !rejectedRecords.has(record)
                && !rejectedConnections.has(`${page}:${record?.id}`))
                .filter(({ page, record }) => !rejectedBadges.has(`${page}:${record?.id}`))
            .map(({ record }) => record),
        errors,
    }
}
